import React, { useState } from 'react';
import {
  ArrowRight,
  ArrowLeft,
  Target,
  Users,
  CheckCircle,
  X,
  Compass,
  Eye,
  Database,
  Cpu,
  Workflow,
  EyeOff,
} from 'lucide-react';

interface TeachingWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

interface WizardStep {
  id: string;
  title: string;
  subtitle: string;
  content: React.ReactNode;
  showUnderTheHood?: boolean;
  underTheHoodContent?: React.ReactNode;
}

export const TeachingWizard: React.FC<TeachingWizardProps> = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showUnderTheHood, setShowUnderTheHood] = useState(false);
  const [userProfile, setUserProfile] = useState({
    who: '',
    role: '',
    facts: [] as string[],
    gaps: [] as string[],
    impacts: [] as string[],
    signals: {
      ContextualAwareness: 0,
      ExecutionRhythms: 0,
      RelationalNavigation: 0,
      MotivationalActivation: 0
    }
  });

  const availableFacts = [
    "We have strategy documents but weekly priorities change mid-stream",
    "Definitions differ across functions (priority, capacity, done)",
    "My calendar/inbox drive the day; I want a repeatable weekly rhythm",
    "Team decisions keep colliding and creating rework",
    "We're firefighting instead of learning and improving",
    "Too many commitments with unclear tradeoffs",
    "Under-utilized talent due to misalignment",
    "Heroic individuals compensate for missing systems"
  ];

  const availableGaps = [
    "Scattered focus",
    "Colliding decisions", 
    "Firefighting overrides learning",
    "Unclear tradeoffs",
    "Too many commitments",
    "Under-utilized talent",
    "Inconsistent execution",
    "Poor stakeholder alignment"
  ];

  const impactTypes = [
    { type: 'financial', label: 'Financial', description: 'Revenue, costs, efficiency' },
    { type: 'strategic', label: 'Strategic', description: 'Competitive advantage, market position' },
    { type: 'cultural', label: 'Cultural', description: 'Team dynamics, trust, engagement' },
    { type: 'personal', label: 'Personal Energy & Relationships', description: 'Work-life balance, stress, relationships' }
  ];

  const steps: WizardStep[] = [
    {
      id: 'welcome',
      title: 'Welcome to Peak Priorities',
      subtitle: 'Let\'s locate you on the map and show you how the system works',
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-24 h-24 bg-gradient-to-r from-primary-100 to-sage-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Compass className="h-12 w-12 text-primary-600" />
            </div>
            <p className="text-lg text-charcoal-700 max-w-2xl mx-auto">
              In the next few minutes, I'll walk you through how Peak Priorities works by having you experience it. 
              We'll start with your situation, then I'll show you the "under the hood" mechanics that make it all work.
            </p>
          </div>
          
          <div className="bg-gradient-to-r from-primary-50 to-sage-50 p-6 border border-primary-200">
            <h3 className="font-bold text-charcoal-900 mb-3">What You'll Learn</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-charcoal-700">
                <CheckCircle className="h-4 w-4 text-primary-600" />
                How to use Outcome Drivers to name problems precisely
              </li>
              <li className="flex items-center gap-2 text-charcoal-700">
                <CheckCircle className="h-4 w-4 text-primary-600" />
                The 12 core capabilities and 4 families
              </li>
              <li className="flex items-center gap-2 text-charcoal-700">
                <CheckCircle className="h-4 w-4 text-primary-600" />
                How the Content Graph and Engines work together
              </li>
              <li className="flex items-center gap-2 text-charcoal-700">
                <CheckCircle className="h-4 w-4 text-primary-600" />
                The MOF architecture (M3, M2, M1, M0) in practice
              </li>
            </ul>
          </div>
        </div>
      ),
      showUnderTheHood: true,
      underTheHoodContent: (
        <div className="space-y-4">
          <h4 className="font-semibold text-charcoal-900">System Architecture Overview</h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-charcoal-50 p-4">
              <h5 className="font-medium text-charcoal-900 mb-2 flex items-center gap-2">
                <Database className="h-4 w-4 text-primary-600" />
                Content Graph
              </h5>
              <p className="text-sm text-charcoal-700">
                All content, templates, and resources are stored with rich metadata that describes their purpose, 
                audience, and relationships to other content.
              </p>
            </div>
            <div className="bg-charcoal-50 p-4">
              <h5 className="font-medium text-charcoal-900 mb-2 flex items-center gap-2">
                <Workflow className="h-4 w-4 text-sage-600" />
                Engines
              </h5>
              <p className="text-sm text-charcoal-700">
                12-16 specialized engines that capture, classify, relate, compose, deliver, measure, and refine content.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'who-are-you',
      title: 'Who Are You?',
      subtitle: 'Let\'s start with your role and context',
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-charcoal-900 mb-3">I'm here for...</h3>
            <div className="grid gap-3">
              {[
                { id: 'individual', label: 'Myself', description: 'Personal productivity and clarity', icon: Target },
                { id: 'team', label: 'My Team/Organization', description: 'Team alignment and execution', icon: Users }
              ].map((option) => {
                const IconComponent = option.icon;
                return (
                  <button
                    key={option.id}
                    onClick={() => setUserProfile(prev => ({ ...prev, who: option.id }))}
                    className={`p-4  border-2 text-left transition-all ${
                      userProfile.who === option.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-charcoal-200 hover:border-charcoal-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <IconComponent className={`h-5 w-5 ${
                        userProfile.who === option.id ? 'text-primary-600' : 'text-charcoal-600'
                      }`} />
                      <div>
                        <h4 className="font-semibold text-charcoal-900">{option.label}</h4>
                        <p className="text-sm text-charcoal-600">{option.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {userProfile.who && (
            <div>
              <h3 className="font-semibold text-charcoal-900 mb-3">Your Role</h3>
              <div className="grid gap-2">
                {[
                  { id: 'IC', label: 'Individual Contributor' },
                  { id: 'Manager', label: 'Manager/Team Lead' },
                  { id: 'Executive', label: 'Executive/Leader' },
                  { id: 'Other', label: 'Other' }
                ].map((role) => (
                  <button
                    key={role.id}
                    onClick={() => setUserProfile(prev => ({ ...prev, role: role.id }))}
                    className={`p-3  border text-left transition-all ${
                      userProfile.role === role.id
                        ? 'border-primary-500 bg-primary-50 text-primary-900'
                        : 'border-charcoal-200 hover:border-charcoal-300'
                    }`}
                  >
                    {role.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ),
      showUnderTheHood: true,
      underTheHoodContent: (
        <div className="space-y-4">
          <h4 className="font-semibold text-charcoal-900">User Profile Creation</h4>
          <div className="bg-charcoal-50 p-4">
            <pre className="text-xs text-charcoal-700 overflow-x-auto">
{`{
  "id": "uuid",
  "who": "${userProfile.who || 'individual|team'}",
  "role": "${userProfile.role || 'IC|Manager|Executive|Other'}",
  "facts": [],
  "gaps": [],
  "impacts": [],
  "signals": {
    "ContextualAwareness": 0,
    "ExecutionRhythms": 0,
    "RelationalNavigation": 0,
    "MotivationalActivation": 0
  }
}`}
            </pre>
          </div>
          <p className="text-sm text-charcoal-700">
            This profile will be used by the Sequencer engine to determine which content and resources to surface.
          </p>
        </div>
      )
    },
    {
      id: 'facts',
      title: 'Current State (Facts)',
      subtitle: 'What\'s factually true right now?',
      content: (
        <div className="space-y-6">
          <div className="bg-[var(--mustard-dim)] p-4 border border-[var(--mustard)]">
            <h3 className="font-semibold text-charcoal-900 mb-2">Guidance</h3>
            <p className="text-sm text-charcoal-700">
              Select any facts that describe your current situation. We're looking for objective truth, 
              not opinions or judgments.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-charcoal-900 mb-3">Choose any that fit:</h3>
            <div className="space-y-2">
              {availableFacts.map((fact, index) => (
                <label key={index} className="flex items-start gap-3 p-3 border border-charcoal-200 hover:bg-charcoal-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={userProfile.facts.includes(fact)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setUserProfile(prev => ({ ...prev, facts: [...prev.facts, fact] }));
                      } else {
                        setUserProfile(prev => ({ ...prev, facts: prev.facts.filter(f => f !== fact) }));
                      }
                    }}
                    className="mt-1"
                  />
                  <span className="text-sm text-charcoal-700">{fact}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      ),
      showUnderTheHood: true,
      underTheHoodContent: (
        <div className="space-y-4">
          <h4 className="font-semibold text-charcoal-900">Capture Engine</h4>
          <div className="bg-charcoal-50 p-4">
            <p className="text-sm text-charcoal-700 mb-3">
              The Capture Engine processes your selections and stores them as structured data:
            </p>
            <pre className="text-xs text-charcoal-700 overflow-x-auto">
{`{
  "facts": [
    ${userProfile.facts.map(fact => `"${fact}"`).join(',\n    ')}
  ]
}`}
            </pre>
          </div>
          <p className="text-sm text-charcoal-700">
            These facts will be used by the Classify Engine to map to our taxonomy and identify relevant content.
          </p>
        </div>
      )
    },
    {
      id: 'gap',
      title: 'Problem/Opportunity (Gap)',
      subtitle: 'What\'s the gap between where you are and where you want to be?',
      content: (
        <div className="space-y-6">
          <div className="bg-[var(--mustard-dim)] p-4 border border-[var(--mustard)]">
            <h3 className="font-semibold text-charcoal-900 mb-2">Guidance</h3>
            <p className="text-sm text-charcoal-700">
              Select the gaps that describe the essence of what's wrong or what could be better. 
              Stay descriptive, not evaluative.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-charcoal-900 mb-3">Choose the gaps that apply:</h3>
            <div className="flex flex-wrap gap-2">
              {availableGaps.map((gap, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (userProfile.gaps.includes(gap)) {
                      setUserProfile(prev => ({ ...prev, gaps: prev.gaps.filter(g => g !== gap) }));
                    } else {
                      setUserProfile(prev => ({ ...prev, gaps: [...prev.gaps, gap] }));
                    }
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    userProfile.gaps.includes(gap)
                      ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]'
                      : 'bg-charcoal-100 text-charcoal-700 border border-charcoal-200 hover:bg-charcoal-200'
                  }`}
                >
                  {gap}
                </button>
              ))}
            </div>
          </div>
        </div>
      ),
      showUnderTheHood: true,
      underTheHoodContent: (
        <div className="space-y-4">
          <h4 className="font-semibold text-charcoal-900">Classify Engine</h4>
          <div className="bg-charcoal-50 p-4">
            <p className="text-sm text-charcoal-700 mb-3">
              The Classify Engine maps your gap selections to our capability taxonomy:
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {userProfile.gaps.map((gap, index) => (
                <div key={index} className="bg-[var(--bg-raised)] p-2 rounded border">
                  <strong>{gap}</strong> → <span className="text-sage-600">Capability Family</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-sm text-charcoal-700">
            This mapping helps the Relate Engine find relevant content and resources.
          </p>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Impact (Why It Matters)',
      subtitle: 'Why does this gap matter now?',
      content: (
        <div className="space-y-6">
          <div className="bg-coral-50 p-4 border border-coral-200">
            <h3 className="font-semibold text-charcoal-900 mb-2">Guidance</h3>
            <p className="text-sm text-charcoal-700">
              Select the types of impact that create urgency. Be specific about consequences or benefits.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-charcoal-900 mb-3">Why does this matter?</h3>
            <div className="grid gap-3">
              {impactTypes.map((impact) => (
                <button
                  key={impact.type}
                  onClick={() => {
                    if (userProfile.impacts.includes(impact.type)) {
                      setUserProfile(prev => ({ ...prev, impacts: prev.impacts.filter(i => i !== impact.type) }));
                    } else {
                      setUserProfile(prev => ({ ...prev, impacts: [...prev.impacts, impact.type] }));
                    }
                  }}
                  className={`p-4  border-2 text-left transition-all ${
                    userProfile.impacts.includes(impact.type)
                      ? 'border-coral-500 bg-coral-50'
                      : 'border-charcoal-200 hover:border-charcoal-300'
                  }`}
                >
                  <h4 className="font-semibold text-charcoal-900">{impact.label}</h4>
                  <p className="text-sm text-charcoal-600">{impact.description}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      ),
      showUnderTheHood: true,
      underTheHoodContent: (
        <div className="space-y-4">
          <h4 className="font-semibold text-charcoal-900">Relate Engine</h4>
          <div className="bg-charcoal-50 p-4">
            <p className="text-sm text-charcoal-700 mb-3">
              The Relate Engine uses your impact selections to find content with matching metadata:
            </p>
            <div className="space-y-2 text-xs">
              {userProfile.impacts.map((impact, index) => (
                <div key={index} className="bg-[var(--bg-raised)] p-2 rounded border">
                  <strong>{impact}</strong> → <span className="text-primary-600">Content with matching impact tags</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-sm text-charcoal-700">
            This creates a personalized content graph that the Compose Engine will use.
          </p>
        </div>
      )
    },
    {
      id: 'capabilities',
      title: 'Capability Signals',
      subtitle: 'Quick diagnostic across the 4 capability families',
      content: (
        <div className="space-y-6">
          <div className="bg-sage-50 p-4 border border-sage-200">
            <h3 className="font-semibold text-charcoal-900 mb-2">Quick Assessment</h3>
            <p className="text-sm text-charcoal-700">
              Rate your current capability level in each family (1 = needs work, 3 = strong).
            </p>
          </div>

          <div className="space-y-4">
            {[
              { key: 'ContextualAwareness', label: 'Contextual Awareness', description: 'Reading the environment and stakeholders' },
              { key: 'ExecutionRhythms', label: 'Execution Rhythms', description: 'Consistent processes and cadences' },
              { key: 'RelationalNavigation', label: 'Relational Navigation', description: 'Working effectively with others' },
              { key: 'MotivationalActivation', label: 'Motivational Activation', description: 'Driving engagement and commitment' }
            ].map((capability) => (
              <div key={capability.key} className="bg-charcoal-50 p-4">
                <h4 className="font-semibold text-charcoal-900 mb-2">{capability.label}</h4>
                <p className="text-sm text-charcoal-600 mb-3">{capability.description}</p>
                <div className="flex gap-2">
                  {[1, 2, 3].map((level) => (
                    <button
                      key={level}
                      onClick={() => setUserProfile(prev => ({
                        ...prev,
                        signals: { ...prev.signals, [capability.key]: level }
                      }))}
                      className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                        userProfile.signals[capability.key as keyof typeof userProfile.signals] === level
                          ? 'bg-primary-600 text-white'
                          : 'bg-[var(--bg-raised)] border border-charcoal-300 text-charcoal-700 hover:bg-charcoal-50'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
      showUnderTheHood: true,
      underTheHoodContent: (
        <div className="space-y-4">
          <h4 className="font-semibold text-charcoal-900">Sequencer Engine</h4>
          <div className="bg-charcoal-50 p-4">
            <p className="text-sm text-charcoal-700 mb-3">
              The Sequencer uses your capability signals to determine content recommendations:
            </p>
            <pre className="text-xs text-charcoal-700 overflow-x-auto">
{`{
  "signals": {
    "ContextualAwareness": ${userProfile.signals.ContextualAwareness},
    "ExecutionRhythms": ${userProfile.signals.ExecutionRhythms},
    "RelationalNavigation": ${userProfile.signals.RelationalNavigation},
    "MotivationalActivation": ${userProfile.signals.MotivationalActivation}
  }
}`}
            </pre>
          </div>
          <p className="text-sm text-charcoal-700">
            Rules engine: If ExecutionRhythms ≤ 2, surface cadence tools before tradeoff playbooks.
          </p>
        </div>
      )
    },
    {
      id: 'basecamp',
      title: 'Your Basecamp Plan',
      subtitle: 'Personalized recommendations based on your profile',
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-primary-50 to-sage-50 p-6 border border-primary-200">
            <h3 className="font-bold text-charcoal-900 mb-4">Your Outcome Driver</h3>
            <div className="space-y-3">
              <div>
                <h4 className="font-semibold text-charcoal-900 mb-1">Current State (Facts)</h4>
                <ul className="text-sm text-charcoal-700">
                  {userProfile.facts.map((fact, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                      {fact}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-900 mb-1">Problem/Opportunity (Gap)</h4>
                <p className="text-sm text-charcoal-700 bg-[var(--mustard-dim)] p-3 border border-[var(--mustard)]">
                  {userProfile.gaps.join(', ')}
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-charcoal-900 mb-1">Impact (Why It Matters)</h4>
                <p className="text-sm text-charcoal-700 bg-coral-50 p-3 border border-coral-200">
                  {userProfile.impacts.map(impact => impactTypes.find(t => t.type === impact)?.label).join(', ')}
                </p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-bold text-charcoal-900 mb-4">Your Personalized Basecamp</h3>
            <div className="grid gap-4">
              {userProfile.who === 'individual' ? (
                <>
                  <div className="bg-[var(--bg-raised)] p-4 border border-charcoal-200">
                    <h4 className="font-semibold text-charcoal-900 mb-2">30-Day Rhythm Install</h4>
                    <p className="text-sm text-charcoal-700 mb-3">Set up your weekly planning and review cadence</p>
                    <button className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 text-sm font-medium transition-colors">
                      Download Template
                    </button>
                  </div>
                  <div className="bg-[var(--bg-raised)] p-4 border border-charcoal-200">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Decision Log Template</h4>
                    <p className="text-sm text-charcoal-700 mb-3">Track your prioritization decisions and learnings</p>
                    <button className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 text-sm font-medium transition-colors">
                      Get Started
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-[var(--bg-raised)] p-4 border border-charcoal-200">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Executive Alignment Session</h4>
                    <p className="text-sm text-charcoal-700 mb-3">Establish common language and shared understanding</p>
                    <button className="bg-sage-600 hover:bg-sage-700 text-white px-4 py-2 text-sm font-medium transition-colors">
                      Schedule Session
                    </button>
                  </div>
                  <div className="bg-[var(--bg-raised)] p-4 border border-charcoal-200">
                    <h4 className="font-semibold text-charcoal-900 mb-2">Team Priority Council Starter Kit</h4>
                    <p className="text-sm text-charcoal-700 mb-3">Set up governance for priority decisions</p>
                    <button className="bg-sage-600 hover:bg-sage-700 text-white px-4 py-2 text-sm font-medium transition-colors">
                      Download Kit
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ),
      showUnderTheHood: true,
      underTheHoodContent: (
        <div className="space-y-4">
          <h4 className="font-semibold text-charcoal-900">Compose & Deliver Engines</h4>
          <div className="bg-charcoal-50 p-4">
            <p className="text-sm text-charcoal-700 mb-3">
              The Compose Engine assembles your Basecamp from content blocks:
            </p>
            <pre className="text-xs text-charcoal-700 overflow-x-auto">
{`{
  "profile_id": "uuid",
  "steps": [
    {
      "type": "template",
      "id": "${userProfile.who === 'individual' ? 'rhythm_install' : 'exec_alignment'}",
      "cta": "Download"
    },
    {
      "type": "assessment", 
      "id": "priorities_assessment_v1",
      "cta": "Begin"
    }
  ]
}`}
            </pre>
          </div>
          <p className="text-sm text-charcoal-700">
            The Deliver Engine renders this as your personalized Basecamp page and can email it to you.
          </p>
        </div>
      )
    }
  ];

  const currentStepData = steps[currentStep];

  const goToNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const goToPrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-charcoal-900/50 backdrop-blur-sm" onClick={onClose}></div>
        
        <div className="relative bg-[var(--bg-raised)] shadow-2xl border border-charcoal-200 w-full max-w-6xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-charcoal-200">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary-100 flex items-center justify-center">
                <Compass className="h-4 w-4 text-primary-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-charcoal-900">{currentStepData.title}</h2>
                <p className="text-sm text-charcoal-600">{currentStepData.subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {currentStepData.showUnderTheHood && (
                <button
                  onClick={() => setShowUnderTheHood(!showUnderTheHood)}
                  className={`px-3 py-1  text-sm font-medium transition-colors ${
                    showUnderTheHood
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-charcoal-100 text-charcoal-700 hover:bg-charcoal-200'
                  }`}
                >
                  {showUnderTheHood ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  {showUnderTheHood ? 'Hide' : 'Show'} Under the Hood
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-charcoal-500 hover:text-charcoal-700 hover:bg-charcoal-100 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="px-6 py-3 bg-charcoal-50 border-b border-charcoal-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-charcoal-700">
                Step {currentStep + 1} of {steps.length}
              </span>
              <span className="text-sm text-charcoal-600">
                {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-charcoal-200 rounded-full h-2">
              <div 
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Content */}
          <div className="flex">
            <div className={`${showUnderTheHood ? 'w-2/3' : 'w-full'} p-6 overflow-y-auto max-h-[60vh]`}>
              {currentStepData.content}
            </div>
            
            {showUnderTheHood && currentStepData.underTheHoodContent && (
              <div className="w-1/3 border-l border-charcoal-200 p-6 bg-charcoal-50 overflow-y-auto max-h-[60vh]">
                <div className="flex items-center gap-2 mb-4">
                  <Cpu className="h-4 w-4 text-charcoal-600" />
                  <h3 className="font-semibold text-charcoal-900">Under the Hood</h3>
                </div>
                {currentStepData.underTheHoodContent}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-charcoal-200 bg-charcoal-50">
            <div className="flex items-center gap-4">
              <button
                onClick={goToPrevious}
                disabled={currentStep === 0}
                className="inline-flex items-center gap-2 px-4 py-2 text-charcoal-600 hover:text-charcoal-900 disabled:text-charcoal-400 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Previous
              </button>
              
              <button
                onClick={onClose}
                className="text-sm text-charcoal-500 hover:text-charcoal-700 transition-colors"
              >
                Skip Tour
              </button>
            </div>

            <button
              onClick={goToNext}
              className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 font-semibold transition-colors"
            >
              {currentStep === steps.length - 1 ? 'Complete' : 'Next'}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
